import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as CheckIconCircle } from '../../../assets/check-icon-circle.svg';
import { ReactComponent as FBIconCircle } from '../../../assets/fb-icon-circle.svg';
import { ReactComponent as RocketIconCircle } from '../../../assets/rocket-icon-circle.svg';
import { ReactComponent as WAIconCircle } from '../../../assets/wa-icon-circle.svg';
import ConnectFacebook from '../../../components/WhatsApp/ConnectFacebook';
import ConnectionOptions from '../../../components/WhatsApp/ConnectionOptions';
import { CONNECTION_OPTIONS } from '../../../components/WhatsApp/constants';
import EligibilityCheck from '../../../components/WhatsApp/EligibilityCheck';
import ExistingConnection from '../../../components/WhatsApp/ExistingConnection';
import FacebookLoginButton from '../../../components/WhatsApp/FacebookLoginButton';
import MigrationRequirements from '../../../components/WhatsApp/MigrationRequirements';
import SupportBanner from '../../../components/WhatsApp/SupportBanner';
import WizardStep from '../../../components/WhatsApp/WizardStep';
import useFBAuthenticated from '../../../hooks/useFBAuthenicated';
import useWhatsAppOnboarding from '../../../hooks/useWhatsAppOnboarding';

const GettingStarted = () => {
  const {
    step,
    setStep,
    eligibilityStep,
    setEligibilityStep,
    connectionOption,
    phoneValidationOption,
    subStep,
    existingStep,
    stepStatus,
    eligibilityStatus,
    handleOptionChange,
    handlePhoneValidationChange,
    handleGetStarted,
    handleEligibilityNext,
    handleExistingNext,
    handleBackStep,
  } = useWhatsAppOnboarding();

  const { access_token } = useSelector((state) => state.auth.user);
  const { isAuthenticated } = useFBAuthenticated(access_token);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginStatus, setLoginStatus] = useState('');
  const navigate = useNavigate();

  const handleLoginSuccess = () => {
    setIsLoggedIn(true);
    setLoginStatus('');
    setStep(4);
  };

  const handleLoginFailed = () => {
    setIsLoggedIn(false);
    setLoginStatus('FAILED');
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <ConnectionOptions
            connectionOption={connectionOption}
            onOptionChange={handleOptionChange}
          />
        );
      case 2:
        if (connectionOption === CONNECTION_OPTIONS.NEW) {
          return (
            <EligibilityCheck
              eligibilityStatus={eligibilityStatus}
              eligibilityStep={eligibilityStep}
              setEligibilityStep={setEligibilityStep}
              phoneValidationOption={phoneValidationOption}
              subStep={subStep}
              onPhoneValidationChange={handlePhoneValidationChange}
            />
          );
        } else {
          return existingStep === 1 ? (
            <ExistingConnection
              connectionOption={connectionOption}
              onOptionChange={handleOptionChange}
            />
          ) : (
            <MigrationRequirements />
          );
        }
      case 3:
      case 4:
        return (
          <ConnectFacebook
            step={step}
            loginStatus={loginStatus}
            isLoggedIn={isLoggedIn}
            onLoginSuccess={handleLoginSuccess}
            onLoginFailed={handleLoginFailed}
            accessToken={access_token}
          />
        );
      default:
        return null;
    }
  };

  const renderNextButton = () => {
    switch (step) {
      case 1:
        return (
          <>
            <Button type="primary" className="get-started-button" onClick={handleGetStarted}>
              Get Started
            </Button>
            <Button type="link" variant="ghost" onClick={() => setStep(3)}>
              Skip and directly connect with Facebook
            </Button>
          </>
        );
      case 2:
        if (connectionOption === CONNECTION_OPTIONS.NEW) {
          if (eligibilityStep === 1) {
            switch (subStep) {
              case 'existing':
                return (
                  <>
                    <Button
                      type="primary"
                      className="get-started-button"
                      onClick={() => setEligibilityStep(2)}
                    >
                      I will migrate the number
                    </Button>
                    <Button type="link" variant="ghost">
                      I will use a new number instead
                    </Button>
                  </>
                );
              case 'none':
                return (
                  <Button
                    type="primary"
                    className="get-started-button"
                    onClick={() => setEligibilityStep(2)}
                  >
                    I will get a new number
                  </Button>
                );
              default:
                return (
                  <>
                    <Button
                      type="primary"
                      className="get-started-button"
                      onClick={handleEligibilityNext}
                    >
                      Next
                    </Button>
                    <Button type="link" variant="ghost">
                      Skip eligibility check
                    </Button>
                  </>
                );
            }
          } else {
            return (
              <>
                <Button type="primary" className="get-started-button" onClick={() => setStep(3)}>
                  Yes, I can access
                </Button>
                <Button type="link" variant="ghost">
                  I will create one later
                </Button>
              </>
            );
          }
        } else {
          if (existingStep === 1) {
            return (
              <>
                <Button type="primary" className="get-started-button" onClick={handleExistingNext}>
                  Next
                </Button>
                <Button type="link" variant="ghost">
                  Skip eligibility check
                </Button>
              </>
            );
          } else {
            return (
              <FacebookLoginButton
                onLoginSuccess={handleLoginSuccess}
                onLoginFailed={handleLoginFailed}
                accessToken={access_token}
                isGettingStarted={false}
                buttonText="Migrate with Facebook"
              />
            );
          }
        }
      case 3:
        return (
          <FacebookLoginButton
            onLoginSuccess={handleLoginSuccess}
            onLoginFailed={handleLoginFailed}
            accessToken={access_token}
            isGettingStarted={false}
            buttonText="Connect with Facebook"
          />
        );
      case 4:
        return (
          <Button type="primary" onClick={() => navigate('/dashboard/whatsapp')}>
            Complete
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <div className="GettingStartedPage">
      <div className="GettingStartedCard">
        <div className="progress-indicator">
          <WizardStep icon={<RocketIconCircle />} text="Get Started" status={stepStatus.step1} />
          <span>—</span>
          <WizardStep
            icon={<WAIconCircle />}
            text="WhatsApp Business Setup"
            status={stepStatus.step2}
          />
          <span>—</span>
          <WizardStep
            icon={<FBIconCircle />}
            text="Connect to Facebook"
            status={stepStatus.step3}
          />
          <span>—</span>
          <WizardStep
            icon={<CheckIconCircle />}
            text="Integration Successful"
            status={stepStatus.step4}
          />
        </div>

        <div className="content-section">{renderStepContent()}</div>
        <div className="button-group">
          <Button className="back-button" onClick={handleBackStep}>
            Back
          </Button>
          <div className="action-buttons">{renderNextButton()}</div>
        </div>
      </div>
      <SupportBanner />
    </div>
  );
};

export default GettingStarted;
